import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useSettingsStore } from '@/store/settings';
import { LS_KEYS } from '@/config';

const STEPS = [
  {
    icon: '🔗',
    title: '去中心化生态',
    body: '没有服务器，没有中心账号。你的身份由 12 个助记词决定，只存在你自己的设备里——这是你在 DP 链生态的通行证。',
  },
  {
    icon: '💬',
    title: '即时通讯',
    body: '端到端加密聊天：只有你和对方能读懂内容，中继只负责转发、看不到任何消息。离线也有邮箱暂存。',
  },
  {
    icon: '🌐',
    title: '内容广场',
    body: '图文会压缩成 CID、签上你的名字后广播上链。每一次发布都是一笔链上交易，自动进入推荐流。',
  },
  {
    icon: '👛',
    title: '去中心化钱包',
    body: '链上资产与身份一体。新身份可领 100 DPB 新手奖励，支持转账、DID 注册，私钥永不上传。',
  },
  {
    icon: '⛏️',
    title: '全行为挖矿',
    body: '不按时间出 DPB，按行为出 DPB：发文、点赞、收藏、分享、浏览、完播、签到、搜索……27 类行为上链即自动增发奖励，无需领取。',
  },
  {
    icon: '🔄',
    title: 'DEX 交易',
    body: '生态内交易大厅：兑换、注入/赎回流动性、限价挂单，链上托管、自动撮合，全部记录可审计。',
  },
  {
    icon: '🛡️',
    title: '安全第一',
    body: '助记词 = 你的全部资产。请离线备份；支持锁定、自动锁定、安全码比对（防中间人）。公共设备用后记得锁定。',
  },
  {
    icon: '🚀',
    title: '开始探索',
    body: '底部导航：聊天 · 广场 · 交易 · 挖矿 · 我的。去发第一条内容、认识新朋友、开始行为挖矿吧！',
  },
];

/** 首启动 4 步新手引导（可跳过，仅首次出现）。 */
export function Onboarding(): JSX.Element {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const theme = useSettingsStore((s) => s.theme);

  const finish = (): void => {
    try {
      localStorage.setItem(LS_KEYS.onboarded, '1');
    } catch {
      // 忽略
    }
    navigate('/welcome', { replace: true });
  };

  const current = STEPS[step];

  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="card w-[min(92vw,460px)] p-6">
        <div className="mb-6 flex items-center justify-between text-xs text-dim">
          <span className="text-accent">MeshLink</span>
          <span className="mono">
            {step + 1}/{STEPS.length}
          </span>
        </div>

        <div className="mb-6 text-center">
          <div className="mb-3 text-4xl" aria-hidden>
            {current.icon}
          </div>
          <h1 className="mb-2 text-lg font-semibold text-text">{current.title}</h1>
          <p className="text-sm leading-relaxed text-dim">{current.body}</p>
        </div>

        <div className="mb-5 flex justify-center gap-1.5">
          {STEPS.map((s, i) => (
            <span
              key={s.title}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-accent' : 'w-1.5 bg-line'}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={finish}>
            跳过
          </Button>
          <div className="flex gap-2">
            {step > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setStep((v) => v - 1)}>
                上一步
              </Button>
            ) : null}
            {step < STEPS.length - 1 ? (
              <Button variant="primary" size="sm" onClick={() => setStep((v) => v + 1)}>
                下一步
              </Button>
            ) : (
              <Button variant="primary" size="sm" onClick={finish}>
                开始使用
              </Button>
            )}
          </div>
        </div>

        <p className="mt-4 text-center text-[11px] text-dim">当前主题：{theme === 'dark' ? '深色' : '浅色'}</p>
      </div>
    </div>
  );
}

export default Onboarding;
